import { create } from "zustand";
// --- API Action Imports ---
// Assuming these functions exist or will be created in ./actions.ts
import {
    discardGen,
    downloadGenerated,
    generateFromCutoff,
    retrieveGenerated,
    retrieveGeneratedMeta,
    retrieveStatus,
    listLibraryFiles,
    getLibraryFile,
    saveGeneratedToLibrary, // Action for saving
    deleteLibraryFile as deleteLibraryFileAction, // Action for deleting (needs to be created in actions.ts)
    cancelGeneration
} from "./actions";
import type { DownloadFilters } from "./downloadFilters";
import { DEFAULT_EXCLUDED_CONTRACT_STATUSES } from "./downloadFilters";

export const STATUS_GEN = "generating";
export const STATUS_UNSAVED = "unsaved";
export const STATUS_LOADING = "loading"
export const STATUS_SAVED = "saved"; // New status
export const STATUS_SAVING = "saving"; // Auto-save in progress
export const STATUS_IDLE = "idle";
export const STATUS_CANCELLED = "cancelled";
export const STATUS_TIMEOUT = "timeout";
export const STATUS_QUEUED = "queued"; // Request queued due to memory throttling
export const STATUS_ERROR = "error";

var timeoutIns : any = null;

export type TypeData = {
    [key:string]:any
}

export type GenerationSummary = {
    installment?: number
    nonInstallment?: number
    individual?: number
    companies?: number
}

export type GeneratedResponse  = {
    Code: string
    Msg: GeneratedData | null | string
    Summary?: GenerationSummary | null
}

export type ValidationRecordType = "Individuals" | "Companies" | "Installments" | "NonInstallments"

export type ValidationResult = {
    clientId: string
    clientName: string
    field: string
    reason: string
    recordType?: ValidationRecordType
}

export type XMLMeta = {
    userId?: string
    filename?: string
    validation?: ValidationResult[]
    summary?: GenerationSummary | null
}

export type GeneratedData = {
    Header: TypeData
    Installments: TypeData[] | null
    NonInstallments: TypeData[] | null
    Companies: TypeData[] | null
    Individuals: TypeData[] | null
    Filename?: string // Optional: Store filename when loaded from library
    CreatedAt?: string // Optional: Store creation date when loaded from library
    Meta?: XMLMeta | null
}

// Structure matching backend XMLLibraryEntry
export type XMLLibraryEntry = {
    filename: string;
    description: string;
    createdAt: string; // ISO 8601 date string
    size: number;
    key: string;
    generatedBy?: {
        username: string;
        displayName: string;
    };
}

// Technical error details for display
export type TechnicalError = {
    statusCode: number;
    timestamp: string;
    duration: number; // in seconds
    endpoint: string;
    rawMessage: string;
}

export type DataGenerationProps = { // Added export
    cutoff: string
    libraryFiles: XMLLibraryEntry[]
    isLoadingLibrary: boolean
    isLoadingFile: boolean
    isSaving: boolean // State for saving indicator
    isDeleting: boolean // State for deleting indicator
    needsLibraryRefresh: boolean
    error: string
    genFromCutoff: (cutoff:string) => Promise<string>
    status: string
    saveProgress: number // Progress percentage for auto-save (0-100)
    generationDuration: number // Duration in seconds
    errorReason: string // Reason for error/cancellation/timeout
    technicalError: TechnicalError | null // Technical error details for developers
    init: () => void
    updateStatus: (loadGenerate?:boolean) => Promise<any>
    generated: GeneratedData | null
    summary: GenerationSummary | null
    discardUnsaved: () => void
    isLoadingGen: boolean    // true when loading generation is in progress
    isLoadingMeta: boolean
    unfocus: () => void // unfocus to any generated data
    downloadGenerated: (raw: boolean, filename: string, libraryFilename?: string, format?: string, submissionType?: number, filters?: DownloadFilters, previewFilename?: string) => Promise<{code:string,message:string|null}>
    fetchLibraryFiles: () => Promise<void>
    loadLibraryFile: (filename: string, cutoff: string) => Promise<string>;
    saveGenerated: () => Promise<string>;
    deleteLibraryFile: (filename: string) => Promise<string>; // Action for deleting
    cancelGeneration: () => Promise<string>;
    generateCocreeFilename: (providerCode: string, reportPeriod: string, submissionAttempt: string, fileCountPart: string, fileNumber: string) => string;
    loadGeneratedMeta: (filename?: string) => Promise<void>;
}

// this is for Extracting summary info from generated data
const deriveSummaryFromData = (data?: GeneratedData | null): GenerationSummary | null => {
    if (!data) return null;
    const summary: GenerationSummary = {};
    if (Array.isArray(data.Installments)) {
        summary.installment = data.Installments.length;
    }
    if (Array.isArray(data.NonInstallments)) {
        summary.nonInstallment = data.NonInstallments.length;
    }
    if (Array.isArray(data.Individuals)) {
        summary.individual = data.Individuals.length;
    }
    if (Array.isArray(data.Companies)) {
        summary.companies = data.Companies.length;
    }
    return Object.keys(summary).length ? summary : null;
};

const normalizeSummary = (summary: unknown): GenerationSummary | null => {
    if (!summary) return null;
    if (typeof summary === "string") {
        try {
            const parsed = JSON.parse(summary);
            return typeof parsed === "object" && parsed !== null ? ({ ...(parsed as GenerationSummary) }) : null;
        } catch {
            return null;
        }
    }
    if (typeof summary === "object") {
        if (Array.isArray(summary)) return null;
        return { ...(summary as GenerationSummary) };
    }
    return null;
};

const mergeSummaries = (primary?: GenerationSummary | null, fallback?: GenerationSummary | null): GenerationSummary | null => {
    const merged: GenerationSummary = { ...(fallback ?? {}) };
    if (primary?.installment !== undefined) merged.installment = primary.installment;
    if (primary?.nonInstallment !== undefined) merged.nonInstallment = primary.nonInstallment;
    if (primary?.individual !== undefined) merged.individual = primary.individual;
    if (primary?.companies !== undefined) merged.companies = primary.companies;
    return Object.keys(merged).length ? merged : null;
};


export const useDataGeneration = create<DataGenerationProps>((set, getState) => ({
    cutoff: "",
    status: "",
    error: "",
    generated: null,
    summary: null,
    libraryFiles: [],
    isLoadingLibrary: false,
    isLoadingFile: false,
    isLoadingGen: false,
    isLoadingMeta: false,
    isSaving: false,
    isDeleting: false, // Initialize deleting state
    needsLibraryRefresh: false,
    saveProgress: 0, // Initialize save progress
    generationDuration: 0,
    errorReason: "",
    technicalError: null, // Initialize technical error
    init() {
        if (getState().status !== "") return
        set(state => ({status:STATUS_IDLE}))
        getState().updateStatus(false)
            .then( (res) => {
                const currentStatus = getState().status
                // Clear stale error/cancelled/timeout states
                if (currentStatus === STATUS_ERROR || currentStatus === STATUS_CANCELLED || currentStatus === STATUS_TIMEOUT) {
                    discardGen()
                    set(state => ({
                        status: STATUS_IDLE,
                        errorReason: "",
                        generationDuration: 0,
                        technicalError: null,
                    }))
                    return
                }
                if (currentStatus === STATUS_GEN || currentStatus === STATUS_SAVING || currentStatus === STATUS_QUEUED) {
                    // If already generating, saving, or queued, start polling
                    timeoutIns = setInterval(() => {
                        getState().updateStatus()
                        const currentStatus = getState().status
                        //console.log("Check status", currentStatus)
                        // Stop polling when generation is complete (unsaved, saved, or error)
                        if (currentStatus === STATUS_UNSAVED ||
                            currentStatus === STATUS_SAVED ||
                            currentStatus === STATUS_ERROR ||
                            currentStatus === STATUS_CANCELLED ||
                            currentStatus === STATUS_TIMEOUT) {
                            clearTimeout(timeoutIns)
                        }
                    }, 1500)
                }
            })
    },
    async genFromCutoff(cutoff) {
        // Prevent new generation if already generating or queued
        if (getState().status === STATUS_GEN || getState().status === STATUS_QUEUED) return Promise.resolve("200")

        // Clear any previous cancelled/timeout/error status
        const currentStatus = getState().status;
        if (currentStatus === STATUS_CANCELLED || currentStatus === STATUS_TIMEOUT || currentStatus === STATUS_ERROR) {
            set(state => ({generationDuration: 0, errorReason: "", technicalError: null}));
        }

        // keep checking status while updating
        clearTimeout(timeoutIns)
        timeoutIns = setInterval(() => {

            getState().updateStatus()
            const currentStatus = getState().status
            //console.log("Check status", currentStatus)
            // Stop polling when generation is complete (unsaved, saved, or error)
            if (currentStatus === STATUS_UNSAVED ||
                currentStatus === STATUS_SAVED ||
                currentStatus === STATUS_ERROR ||
                currentStatus === STATUS_CANCELLED ||
                currentStatus === STATUS_TIMEOUT) {
                clearTimeout(timeoutIns)
            }
        }, 1500)

        set( state => ({status:STATUS_GEN, error:"", generated: null, isLoadingMeta: false}))
        return new  Promise<string>( (res,rej) => {
            generateFromCutoff(cutoff)
            .then( code => {
                if (code === STATUS_SAVED || code === STATUS_UNSAVED || code === STATUS_QUEUED) {
                    set( state => ({status:code, }))
                }
                res(code)
            })
            .catch( err => {
                if (err.response?.status !== 401) {
                    set( state => ({status:STATUS_IDLE, error:err.message}))
                } else {
                    set( state => ({status:STATUS_IDLE}))
                }
                rej(err)
            })
        })
    },
    updateStatus(loadGenerated=true): Promise<any> {
        return new Promise( async (resolve,rej) => {
            retrieveStatus().then( res => {
                //console.log("UPDATE")
                var reason = res.error_reason || "";
                if (reason.length > 300) reason = reason.substring(0,300)
                
                // Parse technical error details if available
                let technicalError: TechnicalError | null = null;
                if (res.connection_error) {
                    technicalError = {
                        statusCode: res.connection_error.status_code || 0,
                        timestamp: res.connection_error.timestamp || new Date().toISOString(),
                        duration: res.connection_error.response_time || 0,
                        endpoint: res.connection_error.endpoint || "",
                        rawMessage: res.connection_error.technical_error || reason,
                    };
                }
                
                const currentState = getState();
                const updates: Partial<DataGenerationProps> = {
                    status: res.status,
                    cutoff: res.cutoff,
                    generationDuration: res.generation_duration || 0,
                    errorReason: reason,
                    technicalError: technicalError,
                    saveProgress: res.save_progress || 0,
                };

                if (currentState.isSaving && res.status !== STATUS_SAVING) {
                    updates.isSaving = false;
                }

                set(() => updates);

                if (currentState.needsLibraryRefresh && res.status !== STATUS_SAVING && res.status !== STATUS_SAVED) {
                    set({ needsLibraryRefresh: false });
                }

                if (res.status === STATUS_SAVED && getState().needsLibraryRefresh) {
                    set({ needsLibraryRefresh: false });
                    getState().fetchLibraryFiles();
                }

                // when the status is unsaved. Retrieved generated data
                const shouldLoadGenerated =
                    res.status === STATUS_UNSAVED ||
                    loadGenerated ||
                    (res.status === STATUS_SAVING && (!currentState.generated || currentState.status !== STATUS_SAVING));

                if (shouldLoadGenerated) {
                    if (getState().isLoadingGen) {
                        resolve(res.status)
                        return
                    }

                    set(() => ({ isLoadingGen: true }))

                    retrieveGenerated().then( res => {
                        //console.log("retrieved",res)
                        let status = getState().status
                        if (status !== STATUS_SAVED) status = res.Code
                        if (status === STATUS_SAVED || status === STATUS_SAVING || status === STATUS_UNSAVED) {
                            const payload = res.Msg as GeneratedData | null
                            const apiSummary = normalizeSummary(res.Summary)
                            const derivedSummary = deriveSummaryFromData(payload)
                            const summary = mergeSummaries(apiSummary, derivedSummary)
                            if (payload) {
                                set(state => ({
                                    status: state.status === STATUS_SAVED && status !== STATUS_SAVED ? state.status : status,
                                    generated: { ...payload },
                                    summary: summary ?? null,
                                }))
                                const filename = payload.Filename ?? undefined
                                getState().loadGeneratedMeta(filename)
                            } else {
                                set(state => ({
                                    status: state.status === STATUS_SAVED && status !== STATUS_SAVED ? state.status : status,
                                    generated: null,
                                    summary: null,
                                }))
                            }
                        }
                        else
                            set( state => ({status:status}))
                        
                        resolve(status)
                        return
                    }).finally( () => {
                        set( state => ({isLoadingGen: false}))
                    })
                }
                resolve(res.status)
            }).catch( (err) => {
                if (err?.response?.status !== 401) {
                    set(() => ({status: STATUS_IDLE, error: err?.message || ""}))
                } else {
                    set(() => ({status: STATUS_IDLE}))
                }
                resolve(STATUS_IDLE)
            })
        })
    },
    discardUnsaved() {
        set( state => ({status:STATUS_IDLE, generated: null, isLoadingMeta: false, summary: null}))
        discardGen()
    },
	async downloadGenerated(raw: boolean, filename: string, cutoff?: string, format?: string, submissionType: number = -1, filters?: DownloadFilters, previewFilename?: string) {
		try {
			const computedFilters: DownloadFilters = filters ?? {
				excludeContractStatuses: [...DEFAULT_EXCLUDED_CONTRACT_STATUSES],
			}
			const res = await downloadGenerated(raw, cutoff, filename, format, submissionType, computedFilters, previewFilename)
            //console.log(res)
            const href = URL.createObjectURL(res.data)
            const link = document.createElement('a')
            link.href = href
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()

            document.body.removeChild(link)
            URL.revokeObjectURL(href)
            return { code: "200", message: null }
        } catch (err: any) {
            console.error("Download error:", err)
            // Extract error message from server response if available
            let errorMessage = "Network error occurred during download";
            
            if (err.response?.data) {
                // Check if response is JSON with Code/Msg structure
                if (err.response.data.Msg) {
                    errorMessage = err.response.data.Msg;
                }
                // Check if response is plain text error
                else if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            return { code: "network_failed", message: errorMessage }
        }
    },
    async loadGeneratedMeta(filename) {
        const stateBefore = getState();
        if (stateBefore.isLoadingMeta) {
            return;
        }
        const snapshot = getState().generated;
        if (!snapshot) {
            return;
        }

        if (stateBefore.status === STATUS_SAVING && snapshot.Meta) {
            return;
        }

        set({ isLoadingMeta: true });

        try {
            const meta = await retrieveGeneratedMeta(filename);
            set(state => {
                if (state.generated !== snapshot) {
                    return { isLoadingMeta: false } as Partial<DataGenerationProps>;
                }

                const metaSummary = meta?.summary ?? null;
                const fallbackSummary = metaSummary
                    ? mergeSummaries(metaSummary, state.summary)
                    : (state.summary ?? deriveSummaryFromData(state.generated));

                return {
                    isLoadingMeta: false,
                    generated: state.generated ? { ...state.generated, Meta: meta ?? null } : null,
                    summary: fallbackSummary,
                };
            });
        } catch (err) {
            console.error("Failed to load generated metadata:", err);
            set(state => {
                if (state.generated !== snapshot) {
                    return { isLoadingMeta: false } as Partial<DataGenerationProps>;
                }

                return {
                    isLoadingMeta: false,
                    generated: state.generated ? { ...state.generated, Meta: null } : null,
                };
            });
        }
    },
    generateCocreeFilename(providerCode, reportPeriod, submissionAttempt, fileCountPart, fileNumber) {
        // Format: XXXXXXXXXX_CSDF_YYYYMMDDAABBCC.zip.gpg
        const fileReference = `${reportPeriod}${submissionAttempt.padStart(2, '0')}${fileCountPart.padStart(2, '0')}${fileNumber.padStart(2, '0')}`;
        return `${providerCode.padStart(10, '0')}_CSDF_${fileReference}.zip.gpg`;
    },
    async fetchLibraryFiles() {
        set({ isLoadingLibrary: true, error: "" });
        try {
            const files = await listLibraryFiles(); // Assumes listLibraryFiles API call exists
            // Sort files by date, newest first
            files.sort((a: XMLLibraryEntry, b: XMLLibraryEntry) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            set({ libraryFiles: files, isLoadingLibrary: false });
        } catch (err: any) {
            console.error("Failed to fetch library files:", err);
            set({ isLoadingLibrary: false, error: err.message || "Failed to load library" });
        }
    },
    async loadLibraryFile(filename: string, cutoff: string) {
        set({ isLoadingFile: true, error: "", status: STATUS_LOADING }); 
        try {
            // Fetch the file content as JSON
            const fileData = await getLibraryFile(filename); // Assumes getLibraryFile API call exists and returns GeneratedData
            //console.log("Loaded library file:", fileData);
            set({
                generated: { ...fileData, Filename: filename, CreatedAt: getState().libraryFiles.find(f => f.filename === filename)?.createdAt }, // Store fetched data
                summary: null,
                status: STATUS_SAVED,
                cutoff: cutoff, // Clear cutoff as it's not relevant for library files
                isLoadingFile: false,
                error: ""
            });
            getState().loadGeneratedMeta(filename);
            return "200"; // Indicate success
        } catch (err: any) {
            console.error(`Failed to load library file ${filename}:`, err);
            set({ isLoadingFile: false, error: err.message || `Failed to load file ${filename}`, status: STATUS_IDLE });
            return err.code || "load_failed"; // Return error code or a generic string
        }
    },
    async saveGenerated() {
        if (getState().status !== STATUS_UNSAVED) {
            console.warn("No unsaved data to save.");
            return "no_data"; // Or appropriate status code
        }
        set({ isSaving: true, error: "", status: STATUS_SAVING });
        try {
            const responseCode = await saveGeneratedToLibrary(); // Call the action
            if (responseCode === "200") {
                set({ needsLibraryRefresh: true });
                return "200";
            } else {
                // Handle specific backend errors if needed
                set({ isSaving: false, status: STATUS_UNSAVED, error: `Failed to save (Code: ${responseCode})` });
                return responseCode;
            }
        } catch (err: any) {
            console.error("Failed to save generated data:", err);
            set({ isSaving: false, status: STATUS_UNSAVED, error: err.message || "Failed to save data" });
            return err.code || "save_failed";
        }
    },
    async deleteLibraryFile(filename: string) {
        set({ isDeleting: true, error: "" });
        try {
            // Assume deleteLibraryFileAction exists in actions.ts and makes the API call
            const responseCode = await deleteLibraryFileAction(filename);
            if (responseCode === "200" || responseCode === "204") { // Check for successful deletion status
                set({ isDeleting: false });
                // Refresh the library file list after successful deletion
                await getState().fetchLibraryFiles();
                return "200";
            } else {
                // Handle potential non-error failure statuses from the backend if needed
                const errorMessage = `Failed to delete ${filename} (Code: ${responseCode})`;
                console.error(errorMessage);
                set({ isDeleting: false, error: errorMessage });
                return responseCode;
            }
        } catch (err: any) {
            const errorMessage = err.message || `Failed to delete file ${filename}`;
            console.error(`Error deleting library file ${filename}:`, err);
            set({ isDeleting: false, error: errorMessage });
            // Rethrow or return a specific error code/string
            return err.code || "delete_failed";
        }
    },
    unfocus() {
        set(state => ({status:STATUS_IDLE, summary: null}))
    },
    async cancelGeneration() {
        try {
            const res = await cancelGeneration();
            clearTimeout(timeoutIns); // Stop the status polling
            set(state => ({status: STATUS_CANCELLED, error: ""}));
            return "200";
        } catch (err: any) {
            console.error("Failed to cancel generation:", err);
            set(state => ({error: err.message || "Failed to cancel generation"}));
            return err.code || "cancel_failed";
        }
    },
}));